import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ColorBlock, SectionHeading, BodyText, BLOCKS, P, PD, PL, A, AL } from './BlogShared.jsx';

/* ─── Panel palette (solid pink-gradient card) ─── */
const G = {
  bg: '#FFFBFC',
  surface: '#FFFFFF',
  card: 'rgba(255,255,255,0.85)',
  cardSolid: '#FFFFFF',
  border: 'rgba(255,45,85,0.10)',
  borderMd: 'rgba(255,45,85,0.16)',
  text: '#1C1028',
  sub: '#3D2E4A',
  muted: '#6B5A7A',
  blur: 'blur(12px)',
  shadow: '0 2px 12px rgba(255,45,85,0.06), 0 1px 3px rgba(0,0,0,0.04)',
  shadowLg: '0 8px 32px rgba(255,45,85,0.10), 0 2px 8px rgba(0,0,0,0.05)',
  panelGradient: `linear-gradient(155deg, #FFF0F3 0%, #FFFFFF 45%, #FFF5F7 100%)`,
};

/* ─── Tier colors using Karobar brand ─── */
const TIERS = {
  owner: { color: P, light: PL, dim: '#F5F0FF', mid: `${P}30`, label: 'Owner', icon: '🛡️', badge: 'Tier 1' },
  staff: { color: A, light: AL, dim: '#FFF5F7', mid: `${A}30`, label: 'Staff', icon: '👥', badge: 'Tier 2' },
  customer: { color: '#34C759', light: '#5DD27A', dim: '#F0FFF4', mid: 'rgba(52,199,89,0.3)', label: 'Customer', icon: '📱', badge: 'Tier 3' },
};

/* Fixed widget height for consistency — smaller on mobile */
const PANEL_H_XS = 320;
const PANEL_H_SM = 440;

/* ─── Tab button ─── */
const TabBtn = ({ tier, active, onClick }) => {
  const t = TIERS[tier];
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.8, px: { xs: 1.8, sm: 2.5 }, py: { xs: 0.9, sm: 1.2 },
        borderRadius: '100px', cursor: 'pointer', whiteSpace: 'nowrap',
        fontSize: { xs: '0.72rem', sm: '0.82rem' }, fontWeight: 600,
        border: active ? `1.5px solid ${t.color}` : '1.5px solid transparent',
        background: active ? t.dim : 'transparent',
        color: active ? t.color : G.muted,
        transition: 'all 0.25s ease',
        '&:hover': { background: active ? t.dim : `${P}05` },
      }}
    >
      {t.icon} {t.label}
    </Box>
  );
};

/* ─── Window chrome bar (light) ─── */
const MockBar = ({ title }) => (
  <Box sx={{
    height: 36, background: '#FFF8F9',
    borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: 1, px: 2,
  }}>
    <Box sx={{ display: 'flex', gap: 0.6, mr: 'auto' }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#FF6059' }} />
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#FFBD2E' }} />
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#28CA42' }} />
    </Box>
    <Typography sx={{ fontSize: '0.68rem', color: G.muted, fontWeight: 500 }}>{title}</Typography>
    <Box sx={{ mr: 'auto' }} />
  </Box>
);

/* ─── Sidebar nav item (light) ─── */
const NavItem = ({ icon, label, active, tierColor }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
    fontSize: '0.7rem', fontWeight: active ? 600 : 500,
    color: active ? tierColor : G.muted,
    background: active ? `${tierColor}10` : 'transparent',
    borderRadius: '6px', mx: 0.5,
    cursor: 'pointer', transition: 'all 0.15s',
    '&:hover': { color: active ? tierColor : G.sub, background: `${P}06` },
  }}>
    <Typography sx={{ fontSize: '0.82rem' }}>{icon}</Typography> {label}
  </Box>
);

/* ─── Stat card (glass) ─── */
const StatCard = ({ label, value, change, positive, accent }) => (
  <Box sx={{
    background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`,
    borderRadius: '10px', p: 1.5, boxShadow: G.shadow,
    transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: G.shadowLg },
  }}>
    <Typography sx={{ fontSize: '0.6rem', color: G.muted, mb: 0.3, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: accent || G.text, mb: 0.3 }}>{value}</Typography>
    <Typography sx={{ fontSize: '0.58rem', fontWeight: 500, color: positive ? '#34C759' : A }}>{change}</Typography>
  </Box>
);

/* ─── Order row (light) ─── */
const OrderRow = ({ id, amount, status, statusColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.8, borderBottom: `1px solid ${G.border}`, '&:last-child': { border: 'none' } }}>
    <Typography sx={{ fontSize: '0.68rem', color: G.muted, fontFamily: 'monospace' }}>{id}</Typography>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: G.text }}>{amount}</Typography>
    <Box sx={{ fontSize: '0.58rem', px: 1, py: '2px', borderRadius: '100px', fontWeight: 600, background: `${statusColor}12`, color: statusColor }}>{status}</Box>
  </Box>
);

/* ─── Weekly sales mini chart (interactive SVG) ─── */
const WeeklyChart = () => {
  const [hovered, setHovered] = useState(null);
  const data = [
    { day: 'Mon', value: 12400 },
    { day: 'Tue', value: 15800 },
    { day: 'Wed', value: 14200 },
    { day: 'Thu', value: 18900 },
    { day: 'Fri', value: 22100 },
    { day: 'Sat', value: 28600 },
    { day: 'Sun', value: 24300 },
  ];
  const max = Math.max(...data.map(d => d.value));
  const points = data.map((d, i) => `${20 + i * 52},${90 - (d.value / max) * 65}`).join(' ');

  return (
    <Box sx={{ background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`, borderRadius: '10px', p: 1.5, boxShadow: G.shadow }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: G.text }}>Weekly Sales</Typography>
        <Typography sx={{ fontSize: '0.58rem', color: G.muted }}>This week</Typography>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <svg viewBox="0 0 380 100" style={{ width: '100%', height: 80, display: 'block' }}>
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={P} stopOpacity="0.15" />
              <stop offset="100%" stopColor={P} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {[30, 55, 80].map(y => (
            <line key={y} x1="10" y1={y} x2="370" y2={y} stroke={`${P}08`} strokeWidth="0.5" />
          ))}
          {/* Area fill */}
          <polygon points={`20,90 ${points} 332,90`} fill="url(#chartFill)" />
          {/* Line */}
          <polyline points={points} fill="none" stroke={P} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* Interactive dots */}
          {data.map((d, i) => (
            <g key={i}>
              <circle
                cx={20 + i * 52} cy={90 - (d.value / max) * 65}
                r={hovered === i ? 5 : 3}
                fill={hovered === i ? A : P}
                stroke="white" strokeWidth="1.5"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              />
              {hovered === i && (
                <g>
                  <rect x={20 + i * 52 - 22} y={90 - (d.value / max) * 65 - 22} width="44" height="16" rx="4" fill={PD} />
                  <text x={20 + i * 52} y={90 - (d.value / max) * 65 - 11} textAnchor="middle" fill="white" fontSize="7" fontWeight="600">₹{(d.value / 1000).toFixed(1)}K</text>
                </g>
              )}
            </g>
          ))}
          {/* Day labels */}
          {data.map((d, i) => (
            <text key={`l${i}`} x={20 + i * 52} y="99" textAnchor="middle" fill={hovered === i ? PD : G.muted} fontSize="7" fontWeight={hovered === i ? '600' : '400'}>{d.day}</text>
          ))}
        </svg>
      </Box>
    </Box>
  );
};

/* ─── Category pie chart (interactive SVG donut) ─── */
const PieChart = () => {
  const [hovered, setHovered] = useState(null);
  const data = [
    { label: 'Starters', value: 28, color: P },
    { label: 'Main', value: 38, color: A },
    { label: 'Drinks', value: 18, color: PL },
    { label: 'Dessert', value: 16, color: '#34C759' },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  let cumulative = 0;

  const arcs = data.map((d, i) => {
    const start = cumulative / total;
    cumulative += d.value;
    const end = cumulative / total;
    const startAngle = start * 2 * Math.PI - Math.PI / 2;
    const endAngle = end * 2 * Math.PI - Math.PI / 2;
    const r = hovered === i ? 42 : 38;
    const ir = 24;
    const x1 = 60 + r * Math.cos(startAngle);
    const y1 = 55 + r * Math.sin(startAngle);
    const x2 = 60 + r * Math.cos(endAngle);
    const y2 = 55 + r * Math.sin(endAngle);
    const ix1 = 60 + ir * Math.cos(endAngle);
    const iy1 = 55 + ir * Math.sin(endAngle);
    const ix2 = 60 + ir * Math.cos(startAngle);
    const iy2 = 55 + ir * Math.sin(startAngle);
    const largeArc = d.value / total > 0.5 ? 1 : 0;
    const path = `M${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} L${ix1},${iy1} A${ir},${ir} 0 ${largeArc} 0 ${ix2},${iy2} Z`;
    return { ...d, path, i };
  });

  return (
    <Box sx={{ background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`, borderRadius: '10px', p: 1.5, boxShadow: G.shadow }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: G.text, mb: 1 }}>Top Categories</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <svg viewBox="0 0 120 110" style={{ width: 100, height: 90, flexShrink: 0 }}>
          {arcs.map((arc) => (
            <path
              key={arc.i}
              d={arc.path}
              fill={arc.color}
              opacity={hovered === null || hovered === arc.i ? 1 : 0.4}
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={() => setHovered(arc.i)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          <text x="60" y="53" textAnchor="middle" fill={G.text} fontSize="9" fontWeight="700">
            {hovered !== null ? `${data[hovered].value}%` : '84'}
          </text>
          <text x="60" y="62" textAnchor="middle" fill={G.muted} fontSize="6">
            {hovered !== null ? data[hovered].label : 'orders'}
          </text>
        </svg>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, flex: 1 }}>
          {data.map((d, i) => (
            <Box
              key={d.label}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer',
                opacity: hovered === null || hovered === i ? 1 : 0.5,
                transition: 'all 0.15s',
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '2px', background: d.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.6rem', color: G.sub, flex: 1 }}>{d.label}</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: G.text }}>{d.value}%</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/* ─── Menu card (glass) ─── */
const MenuCard = ({ name, price, category, btnColor }) => (
  <Box sx={{
    background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`,
    borderRadius: '10px', p: 1.5, boxShadow: G.shadow,
    transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: G.shadowLg },
  }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: G.text, mb: 0.3 }}>{name}</Typography>
    <Typography sx={{ fontSize: '0.68rem', color: G.muted, mb: 1.2 }}>{price} · {category}</Typography>
    <Box sx={{
      width: '100%', py: 0.7, borderRadius: '6px', border: `1px solid ${btnColor}30`,
      textAlign: 'center', fontSize: '0.65rem', fontWeight: 600, color: btnColor,
      cursor: 'pointer', transition: 'all 0.2s',
      '&:hover': { background: `${btnColor}10`, borderColor: btnColor, transform: 'scale(1.02)' },
    }}>
      + Add to order
    </Box>
  </Box>
);

/* ════════════════════════════════════════════════
   OWNER PANEL (expanded with charts)
   ════════════════════════════════════════════════ */
const OwnerPanel = () => (
  <Box sx={{
    border: `1px solid ${G.border}`, borderRadius: '16px', overflow: 'hidden',
    background: G.panelGradient,
    boxShadow: G.shadowLg, height: { xs: PANEL_H_XS, sm: PANEL_H_SM },
  }}>
    <MockBar title="Karobar — Owner view" />
    <Box sx={{ display: 'flex', height: { xs: PANEL_H_XS - 36, sm: PANEL_H_SM - 36 } }}>
      {/* Sidebar */}
      <Box sx={{
        width: 120, flexShrink: 0, background: '#FFF8FA',
        borderRight: `1px solid ${G.border}`, py: 1.5, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.3,
      }}>
        <NavItem icon="📊" label="Overview" active tierColor={TIERS.owner.color} />
        <NavItem icon="📈" label="Analytics" />
        <NavItem icon="📋" label="Orders" />
        <NavItem icon="👥" label="Staff" />
        <NavItem icon="🍽️" label="Menu" />
        <NavItem icon="⚙️" label="Settings" />
      </Box>
      {/* Main */}
      <Box sx={{ flex: 1, p: 1.5, background: G.bg, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
        {/* Stats row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 0.8 }}>
          <StatCard label="Today's revenue" value="₹18,420" change="↑ 12% vs yesterday" positive accent={P} />
          <StatCard label="Total orders" value="84" change="↑ 6 more today" positive accent={PD} />
          <StatCard label="Avg order value" value="₹219" change="↓ ₹8 from avg" positive={false} accent={P} />
          <StatCard label="Staff on duty" value="5" change="All active" positive accent={PL} />
        </Box>
        {/* Charts row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' }, gap: 0.8 }}>
          <WeeklyChart />
          <PieChart />
        </Box>
        {/* Orders & Staff */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
          <Box sx={{ background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`, borderRadius: '10px', p: 1.5, boxShadow: G.shadow }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: G.text }}>Recent orders</Typography>
              <Typography sx={{ fontSize: '0.58rem', color: P, fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>View all →</Typography>
            </Box>
            <OrderRow id="#1084" amount="₹340" status="Done" statusColor="#34C759" />
            <OrderRow id="#1083" amount="₹185" status="Cooking" statusColor="#EF9F27" />
            <OrderRow id="#1082" amount="₹520" status="New" statusColor={P} />
            <OrderRow id="#1081" amount="₹210" status="Done" statusColor="#34C759" />
          </Box>
          <Box sx={{ background: G.card, backdropFilter: G.blur, border: `1px solid ${G.border}`, borderRadius: '10px', p: 1.5, boxShadow: G.shadow }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: G.text }}>Staff on duty</Typography>
              <Typography sx={{ fontSize: '0.58rem', color: P, fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Manage →</Typography>
            </Box>
            {[
              { i: 'RK', n: 'Ravi Kumar', r: 'Waiter', c: P },
              { i: 'PM', n: 'Priya Mehta', r: 'Cashier', c: A },
              { i: 'AS', n: 'Anil Sharma', r: 'Kitchen', c: '#34C759' },
              { i: 'NJ', n: 'Neha Joshi', r: 'Waiter', c: PL },
            ].map(s => (
              <Box key={s.i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, borderBottom: `1px solid ${G.border}`, '&:last-child': { border: 'none' } }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: `${s.c}15`, color: s.c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 700 }}>{s.i}</Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: G.text, flex: 1 }}>{s.n}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: G.muted }}>{s.r}</Typography>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#34C759' }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ─── Karobar-style item card (mini version — ~1:1 aspect ratio like home) ─── */
const KarobarCard = ({ name, price, category, vegType, selected }) => {
  const catColors = vegType === 'veg'
    ? { bg: '#DCFCE7', text: '#166534', label: 'Veg' }
    : vegType === 'nonveg'
    ? { bg: '#FEE2E2', text: '#B91C1C', label: 'Non Veg' }
    : { bg: 'rgba(59,130,246,0.1)', text: '#2563EB', label: category };
  return (
    <Box sx={{
      borderRadius: '14px', p: 1.2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      aspectRatio: '1 / 1', position: 'relative',
      cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.3s',
      border: selected ? 'none' : '1px solid rgba(0,0,0,0.10)',
      background: selected
        ? `linear-gradient(135deg, ${PL} 0%, ${A} 100%)`
        : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
      boxShadow: selected
        ? `0 6px 20px ${A}30, 0 2px 6px ${PL}20`
        : '0 2px 8px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)',
      '&:hover': { transform: 'scale(1.03)' },
    }}>
      <Box>
        <Box sx={{
          display: 'inline-block', px: 0.6, py: 0.2, borderRadius: '8px', fontSize: '0.5rem',
          fontWeight: 600, letterSpacing: '0.5px', lineHeight: 1.4,
          background: selected ? 'rgba(255,255,255,0.2)' : catColors.bg,
          color: selected ? '#fff' : catColors.text,
        }}>{catColors.label}</Box>
        <Typography sx={{
          mt: 0.6, fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3,
          color: selected ? '#fff' : G.text,
        }}>{name}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', px: 1, py: 0.3,
          borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700,
          background: selected ? 'rgba(255,255,255,0.15)' : 'rgba(22,163,74,0.1)',
          border: `1px solid ${selected ? 'rgba(255,255,255,0.3)' : 'rgba(22,163,74,0.2)'}`,
          color: selected ? '#fff' : '#16A34A',
        }}>₹{price}</Box>
      </Box>
      {selected && (
        <Box sx={{
          position: 'absolute', top: 5, right: 5, width: 18, height: 18, borderRadius: '50%',
          background: '#fff', color: PD, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.6rem', fontWeight: 800, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        }}>1</Box>
      )}
    </Box>
  );
};

/* ════════════════════════════════════════════════
   STAFF PANEL (Karobar-style cards + order confirmation)
   ════════════════════════════════════════════════ */
const StaffPanel = () => (
  <Box sx={{
    border: `1px solid ${G.border}`, borderRadius: '16px', overflow: 'hidden',
    background: G.panelGradient,
    boxShadow: G.shadowLg, height: { xs: PANEL_H_XS, sm: PANEL_H_SM },
  }}>
    <MockBar title="Karobar — Staff view" />
    <Box sx={{ display: 'flex', height: { xs: PANEL_H_XS - 36, sm: PANEL_H_SM - 36 } }}>
      {/* Sidebar */}
      <Box sx={{
        width: 100, flexShrink: 0, background: '#FFF8FA',
        borderRight: `1px solid ${G.border}`, py: 1.5, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.3,
      }}>
        <NavItem icon="➕" label="New order" active tierColor={TIERS.staff.color} />
        <NavItem icon="📋" label="Active" />
        <NavItem icon="🍽️" label="Menu" />
      </Box>
      {/* Main content — split: left=menu cards, right=order panel */}
      <Box sx={{ flex: 1, display: 'flex', background: G.bg, overflow: 'hidden' }}>
        {/* Left: Karobar-style menu cards */}
        <Box sx={{ flex: 1, p: 1.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Table indicator */}
          <Box sx={{
            background: G.card, border: `1px solid ${G.border}`, borderRadius: '8px',
            px: 1.2, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.8,
          }}>
            <Typography sx={{ fontSize: '0.65rem', color: G.muted }}>Serving</Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: G.text }}>Table 4</Typography>
          </Box>
          {/* Item cards grid — responsive columns */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 0.6, position: 'relative' }}>
            <KarobarCard name="Paneer Tikka" price="220" category="Starter" vegType="veg" selected />
            <KarobarCard name="Dal Makhani" price="180" category="Main" vegType="veg" />
            <KarobarCard name="Chicken Curry" price="260" category="Main" vegType="nonveg" />
            <KarobarCard name="Mango Lassi" price="90" category="Drink" vegType={null} />
            <KarobarCard name="Butter Naan" price="45" category="Bread" vegType="veg" />
            <KarobarCard name="Gulab Jamun" price="80" category="Dessert" vegType="veg" />
            <KarobarCard name="Tandoori Roti" price="30" category="Bread" vegType="veg" />
            <KarobarCard name="Masala Chai" price="40" category="Drink" vegType={null} />
          </Box>
          {/* Active orders section */}
          <Box sx={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: '10px', p: 1.2 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: G.text, mb: 0.6 }}>Active Orders</Typography>
            {[
              { id: '#1084', table: 'T3', status: 'Cooking', sc: '#EF9F27' },
              { id: '#1085', table: 'T7', status: 'Ready', sc: '#34C759' },
            ].map(o => (
              <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: `1px solid ${G.border}`, '&:last-child': { border: 'none' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: G.muted }}>{o.id}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: G.sub }}>{o.table}</Typography>
                </Box>
                <Box sx={{ fontSize: '0.55rem', px: 0.8, py: '2px', borderRadius: '100px', fontWeight: 600, background: `${o.sc}15`, color: o.sc }}>{o.status}</Box>
              </Box>
            ))}
          </Box>
        </Box>
        {/* Right: Order confirmation panel */}
        <Box sx={{
          width: { xs: 120, sm: 155 }, flexShrink: 0, borderLeft: `1px solid ${G.border}`,
          background: '#FFFFFF', p: { xs: 0.8, sm: 1.2 }, display: 'flex', flexDirection: 'column',
        }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: G.text, mb: 0.5 }}>Current Order</Typography>
          <Typography sx={{ fontSize: '0.58rem', color: G.muted, mb: 1 }}>Table 4 · 3 items</Typography>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {[['Paneer Tikka', '₹220', '×1'], ['Dal Makhani', '₹180', '×1'], ['Butter Naan', '₹90', '×2']].map(([item, price, qty]) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.4, borderBottom: `1px solid ${G.border}` }}>
                <Box>
                  <Typography sx={{ fontSize: '0.62rem', color: G.text, fontWeight: 500 }}>{item}</Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: G.muted }}>{qty}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: G.sub }}>{price}</Typography>
              </Box>
            ))}
          </Box>
          {/* Total & CTA */}
          <Box sx={{ mt: 'auto', pt: 1, borderTop: `1px solid ${G.borderMd}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: G.text }}>Total</Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: G.text }}>₹490</Typography>
            </Box>
            <Box sx={{
              py: 0.9, borderRadius: '8px', background: TIERS.staff.color, textAlign: 'center',
              fontSize: '0.68rem', fontWeight: 700, color: '#fff', cursor: 'pointer',
              boxShadow: `0 4px 12px ${TIERS.staff.color}30`,
              transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 6px 20px ${TIERS.staff.color}40` },
            }}>
              Place Order →
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ════════════════════════════════════════════════
   CUSTOMER PANEL (QR highlight + phone mockup)
   ════════════════════════════════════════════════ */
const CustomerPanel = () => (
  <Box sx={{
    border: `1px solid ${G.border}`, borderRadius: '16px', overflow: 'hidden',
    background: G.panelGradient,
    boxShadow: G.shadowLg, height: { xs: PANEL_H_XS, sm: PANEL_H_SM },
  }}>
    <Box sx={{ display: 'flex', height: { xs: PANEL_H_XS, sm: PANEL_H_SM }, overflow: 'hidden' }}>
      {/* Left sub-section: QR code feature highlight */}
      <Box sx={{
        flex: '0 0 45%', p: 2.5, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', textAlign: 'center',
        borderRight: `1px solid ${G.border}`, background: '#FEFCFD',
      }}>
        {/* QR code visual */}
        <Box sx={{
          width: 80, height: 80, borderRadius: '16px', mb: 2,
          background: '#fff', border: `2px solid ${P}20`,
          boxShadow: `0 4px 20px ${P}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          {/* Mini QR pattern */}
          <svg viewBox="0 0 60 60" width="52" height="52">
            <rect x="4" y="4" width="16" height="16" rx="3" fill={PD} />
            <rect x="40" y="4" width="16" height="16" rx="3" fill={PD} />
            <rect x="4" y="40" width="16" height="16" rx="3" fill={PD} />
            <rect x="7" y="7" width="10" height="10" rx="2" fill="#fff" />
            <rect x="43" y="7" width="10" height="10" rx="2" fill="#fff" />
            <rect x="7" y="43" width="10" height="10" rx="2" fill="#fff" />
            <rect x="9" y="9" width="6" height="6" rx="1" fill={PD} />
            <rect x="45" y="9" width="6" height="6" rx="1" fill={PD} />
            <rect x="9" y="45" width="6" height="6" rx="1" fill={PD} />
            <rect x="24" y="4" width="4" height="4" fill={A} />
            <rect x="24" y="12" width="4" height="4" fill={PD} />
            <rect x="32" y="8" width="4" height="4" fill={PL} />
            <rect x="24" y="24" width="4" height="4" fill={PD} />
            <rect x="30" y="24" width="4" height="4" fill={A} />
            <rect x="36" y="24" width="4" height="4" fill={PD} />
            <rect x="24" y="32" width="4" height="4" fill={PL} />
            <rect x="40" y="32" width="4" height="4" fill={PD} />
            <rect x="48" y="32" width="4" height="4" fill={A} />
            <rect x="40" y="40" width="4" height="4" fill={PD} />
            <rect x="48" y="44" width="4" height="4" fill={PL} />
            <rect x="44" y="48" width="4" height="4" fill={A} />
            <rect x="52" y="52" width="4" height="4" fill={PD} />
          </svg>
        </Box>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: PD, mb: 0.5 }}>
          Scan & Order
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: G.muted, lineHeight: 1.5, mb: 2, maxWidth: 160 }}>
          No app. No login. Customers scan the table QR and order directly.
        </Typography>
        {/* Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 160 }}>
          {[
            { icon: '📱', text: 'Scan QR at table' },
            { icon: '🍽️', text: 'Browse & select items' },
            { icon: '✅', text: 'Order sent to kitchen' },
          ].map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left' }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: TIERS.customer.dim, border: `1px solid ${TIERS.customer.mid}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem',
              }}>{step.icon}</Box>
              <Typography sx={{ fontSize: '0.63rem', color: G.sub, fontWeight: 500 }}>{step.text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right sub-section: Mobile phone mockup */}
      <Box sx={{
        flex: '0 0 55%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 2, background: G.bg,
      }}>
        {/* Phone frame */}
        <Box sx={{
          width: 185, height: { xs: PANEL_H_XS - 40, sm: PANEL_H_SM - 40 }, borderRadius: '24px',
          border: '3px solid #1C1028', background: '#FFFFFF',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
          position: 'relative',
        }}>
          {/* Phone notch */}
          <Box sx={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: 60, height: 14, background: '#1C1028', borderRadius: '0 0 10px 10px',
            zIndex: 2,
          }} />
          {/* Phone header */}
          <Box sx={{
            pt: 2.5, pb: 0.8, px: 1.5, background: PD,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', letterSpacing: '0.5px' }}>
              MENU
            </Typography>
          </Box>
          {/* Table badge */}
          <Box sx={{ px: 1.2, py: 0.5, background: `${P}08` }}>
            <Typography sx={{ fontSize: '0.5rem', color: PD, fontWeight: 600, textAlign: 'center' }}>
              📍 Table 7 · Dine-in
            </Typography>
          </Box>
          {/* Karobar-style item cards (mini grid) */}
          <Box sx={{ flex: 1, px: 0.8, py: 0.8, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5, overflow: 'hidden', alignContent: 'start' }}>
            {[
              { name: 'Veg Biryani', price: '200', vegType: 'veg' },
              { name: 'Chicken Curry', price: '260', vegType: 'nonveg' },
              { name: 'Cold Coffee', price: '110', vegType: null, cat: 'Drink' },
              { name: 'Garlic Bread', price: '70', vegType: 'veg' },
            ].map((item, idx) => {
              const cat = item.vegType === 'veg'
                ? { bg: '#DCFCE7', color: '#166534', label: 'Veg' }
                : item.vegType === 'nonveg'
                ? { bg: '#FEE2E2', color: '#B91C1C', label: 'Non Veg' }
                : { bg: 'rgba(59,130,246,0.1)', color: '#2563EB', label: item.cat };
              const sel = idx === 0;
              return (
                <Box key={item.name} sx={{
                  borderRadius: '10px', p: 0.8, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  aspectRatio: '1 / 1', position: 'relative',
                  border: sel ? 'none' : '1px solid rgba(0,0,0,0.10)',
                  background: sel
                    ? `linear-gradient(135deg, ${PL} 0%, ${A} 100%)`
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  boxShadow: sel
                    ? `0 3px 10px ${A}25`
                    : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  <Box>
                    <Box sx={{
                      display: 'inline-block', px: 0.4, py: 0.15, borderRadius: '5px', fontSize: '0.4rem',
                      fontWeight: 600, background: sel ? 'rgba(255,255,255,0.2)' : cat.bg, color: sel ? '#fff' : cat.color,
                    }}>{cat.label}</Box>
                    <Typography sx={{ mt: 0.3, fontWeight: 600, fontSize: '0.52rem', lineHeight: 1.2, color: sel ? '#fff' : G.text }}>{item.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{
                      px: 0.5, py: 0.2, borderRadius: '6px', fontSize: '0.5rem', fontWeight: 700,
                      background: sel ? 'rgba(255,255,255,0.15)' : 'rgba(22,163,74,0.1)',
                      color: sel ? '#fff' : '#16A34A',
                      border: `1px solid ${sel ? 'rgba(255,255,255,0.3)' : 'rgba(22,163,74,0.2)'}`,
                    }}>₹{item.price}</Box>
                  </Box>
                  {sel && (
                    <Box sx={{
                      position: 'absolute', top: 3, right: 3, width: 12, height: 12, borderRadius: '50%',
                      background: '#fff', color: PD, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.4rem', fontWeight: 800,
                    }}>1</Box>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Place order footer */}
          <Box sx={{ px: 1, py: 0.8, borderTop: `1px solid ${G.border}`, background: '#FAFAFA' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
              <Typography sx={{ fontSize: '0.5rem', color: G.muted }}>1 item</Typography>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: G.text }}>₹200</Typography>
            </Box>
            <Box sx={{
              py: 0.6, borderRadius: '8px', background: TIERS.customer.color, textAlign: 'center',
              fontSize: '0.55rem', fontWeight: 700, color: '#fff',
              boxShadow: `0 3px 10px ${TIERS.customer.color}30`,
            }}>
              Place Order →
            </Box>
          </Box>
          {/* Phone home indicator */}
          <Box sx={{
            width: 36, height: 3, borderRadius: '100px', background: '#D1D5DB',
            mx: 'auto', my: 0.5,
          }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

/* ─── Tier info ─── */
const tierInfo = [
  {
    key: 'owner',
    headline: 'Owner dashboard',
    chips: ['Revenue & insights', 'Staff management', 'All orders', 'Menu editor', 'Full settings'],
  },
  {
    key: 'staff',
    headline: 'Staff access',
    chips: ['Place orders', 'View active orders', 'Browse menu', 'Table management'],
  },
  {
    key: 'customer',
    headline: 'Customer self-order',
    chips: ['Scan QR at table', 'Browse & add items', 'Live order tracking', 'No app needed'],
  },
];

const panels = { owner: OwnerPanel, staff: StaffPanel, customer: CustomerPanel };

/* ════════════════════════════════════════════════════════════════
   MAIN EXPORT
   ════════════════════════════════════════════════════════════════ */
export const AccessTiersSection = () => {
  const [active, setActive] = useState(0);
  const info = tierInfo[active];
  const t = TIERS[info.key];
  const Panel = panels[info.key];

  return (
    <ColorBlock bg={BLOCKS.slate} innerBg="white" innerRadius="32px">
      {/* Section header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
        <SectionHeading size="lg" align="center" sx={{ color: PD, mb: 2 }}>
          One POS,{' '}
          <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic', background: `linear-gradient(90deg, ${P}, ${A})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            three roles
          </Box>
          {' '}— everyone gets <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>exactly</Box> what they need
        </SectionHeading>
        <BodyText align="center" sx={{ maxWidth: 520, mx: 'auto' }}>
          Karobar's role-based access keeps your restaurant running smoothly.
        </BodyText>
      </Box>

      {/* Tabs — pill strip */}
      <Box sx={{
        display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 0.8 }, mb: { xs: 4, md: 5 }, flexWrap: 'nowrap',
        p: 0.6, background: '#FFF5F7',
        borderRadius: '100px', border: `1px solid ${G.border}`,
        boxShadow: G.shadow, width: 'fit-content', mx: 'auto',
      }}>
        {tierInfo.map((ti, i) => (
          <TabBtn key={ti.key} tier={ti.key} active={i === active} onClick={() => setActive(i)} />
        ))}
      </Box>

      {/* Content: Left info + Right widget */}
      <Box
        key={info.key}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 0, md: 4 },
          alignItems: 'flex-start',
          animation: 'fadeIn 0.4s ease',
          '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}
      >
        {/* Left: Info */}
        <Box sx={{ flex: '0 0 180px', minWidth: 0, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px',
              background: t.dim, border: `1px solid ${t.mid}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
              boxShadow: `0 2px 8px ${t.color}15`,
            }}>
              {t.icon}
            </Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: PD }}>
              {info.headline}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {info.chips.map((chip) => (
              <Box key={chip} sx={{
                px: 1.8, py: 0.5, borderRadius: '100px', fontSize: '0.7rem', fontWeight: 500,
                border: `1px solid ${t.color}20`, color: t.color, background: t.dim,
                transition: 'all 0.15s', '&:hover': { background: `${t.color}15` },
              }}>
                {chip}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right: Mockup widget — fixed-size frame (mobile), widget fit inside via flex */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            // Fixed container — identical width & height for all three tiers on mobile
            height: { xs: PANEL_H_XS, sm: 'auto' },
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', '& > *': { flex: 1, width: '100%' } }}>
            <Panel />
          </Box>
        </Box>
      </Box>
    </ColorBlock>
  );
};

